import React from 'react';
import { Record } from 'immutable';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { WorkspaceMeetingMode } from 'Shared/types/WorkspaceMeetingMode.type';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Utils
import * as MeetingItemUtils from 'Shared/utils/MeetingItem.utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useMeetingModeTracking } from 'Shared/tracking/use-meeting-mode-tracking';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';
import * as UserHelper from 'Shared/model_helpers/User.helpers';

// Actions
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';

// Selectors
import * as meetingItemSelectors from 'Shared/selectors/MeetingItems.selectors';
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import { WpContainer, WpContainerHeader, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpAvatar } from 'Wpds/components/avatar';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import MeetingItemInput from 'Shared/components/MeetingItemInput/MeetingItemInput';
import AttachmentsContainer from 'Shared/components/Attachments/AttachmentsContainer';
import { WpCheckbox } from 'Wpds/components/checkbox';
import RevisitDropDown from 'Shared/components/MeetingItemDetailPanel/RevisitDropDown/RevisitDropDown';
import AssignOwnerDropdown from 'Shared/components/MeetingItemDetailPanel/info/AssignOwnerDropdown';

// Styles
import './TopicDetails.scss';

type Props = {
  meetingModeSession: Record<WorkspaceMeetingMode>;
  handleNavigateToItem: Function;
  meetingItemOrderedKeys: Array<string>;
};

const TopicDetails = ({ meetingModeSession, handleNavigateToItem, meetingItemOrderedKeys }: Props) => {
  const dispatch = useDispatch();
  const { trackNextClick, trackPreviousClick } = useMeetingModeTracking();
  const [isNavigatingForward, toggleIsNavigatingFoward] = useToggle(false);
  const [isNavigatingBack, toggleIsNavigatingBack] = useToggle(false);

  const { isSmall } = useResponsiveScreenWidth({ isAtMost: true });

  const meetingItemId = WorkspaceMeetingModeHelper.getWorkspaceMeetingItemUuid(meetingModeSession);

  const meetingItem = useSelector(state =>
    meetingItemSelectors.getMeetingItemByUuid(state, { meetingItemUuid: meetingItemId })
  ).get(meetingItemId);

  const parentMeetingItemUuid = MeetingItemHelpers.getParentUuid(meetingItem);
  const parentMeetingItem = useSelector(state =>
    meetingItemSelectors.getMeetingItemByUuid(state, { meetingItemUuid: parentMeetingItemUuid })
  ).get(parentMeetingItemUuid);

  const isCompleted = MeetingItemHelpers.getCompleted(meetingItem);
  const hasParentItem = MeetingItemUtils.hasParentItem(meetingItem);

  const assigneeId = MeetingItemHelpers.getAssigneeId(meetingItem);

  const assignee = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: assigneeId }));
  const assigneeAvatar = UserHelper.getAvatar(assignee);
  const assigneeDisplayName = UserHelper.getDisplayName(assignee);
  const hasAssignee = Boolean(assignee);

  const attachments = MeetingItemHelpers.getAttachments(meetingItem);
  const hasAttachments = MeetingItemUtils.hasAttachments(attachments);
  const showAttachmentsAsList = hasAttachments && attachments.size > 2;

  const isFirstItem = meetingItemOrderedKeys[0] === meetingItemId;
  const isPreviousDisabled =
    isFirstItem || (meetingItemOrderedKeys[0] === parentMeetingItemUuid && meetingItemOrderedKeys[1] === meetingItemId);
  const isLastItem = meetingItemOrderedKeys[meetingItemOrderedKeys.length - 1] === meetingItemId;
  const [isCompleteChecked, toggleIsCompleteChecked] = useToggle(isCompleted);

  const handleChangeComplete = callback => {
    if (isCompleteChecked !== isCompleted) {
      const data = {
        item: {
          completed: isCompleteChecked,
        },
        [QueryParamConstants.Api.DATA_FORMAT]: QueryParamConstants.Api.JSONAPI,
      };
      dispatch(meetingItemActions.updateCompleteStatus(meetingItemId, data)).then(() => {
        callback();
      });
    } else {
      callback();
    }
  };

  const handlePreviousItemClick = () => {
    toggleIsNavigatingBack();
    const previousItemUuid = meetingItemOrderedKeys[meetingItemOrderedKeys.indexOf(meetingItemId) - 1];
    trackPreviousClick();
    handleNavigateToItem(previousItemUuid, 'back', () => {
      toggleIsNavigatingBack();
    });
  };

  const handleNextItemClick = () => {
    toggleIsNavigatingFoward();
    handleChangeComplete(() => {
      const nextItemUuid = isLastItem
        ? 'wrapup'
        : meetingItemOrderedKeys[meetingItemOrderedKeys.indexOf(meetingItemId) + 1];
      trackNextClick({ isCompleteChecked });
      handleNavigateToItem(nextItemUuid, 'next', () => {
        toggleIsNavigatingFoward();
      });
    });
  };

  const baseClassName = 'TopicDetails';

  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header`}>
          <div className={`${baseClassName}__header__leader`}>
            {hasAssignee ? (
              <>
                <WpAvatar size="sm" avatar={assigneeAvatar.toJS()} />
                <span className={`${baseClassName}__header__leader__label`}>{assigneeDisplayName} is leading</span>
              </>
            ) : (
              <AssignOwnerDropdown
                meetingItem={meetingItem}
                selectedUserCustomLabel={`${assigneeDisplayName} is leading`}
              />
            )}
          </div>
          {!isSmall && (
            <div className={`${baseClassName}__header__recurring`}>
              <div className={`${baseClassName}__header__recurring__icon`}>
                <WpIcon name="recycle" size="sm" />
              </div>

              <RevisitDropDown meetingItem={meetingItem} placement="bottom-end" />
            </div>
          )}
        </div>
      </WpContainerHeader>
      <WpContainerBody>
        <div className={`${baseClassName}__body`}>
          {hasParentItem && (
            <div className={`${baseClassName}__body__parent`}>
              <MeetingItemInput meetingItem={parentMeetingItem} readonly hideDescription />
            </div>
          )}
          <div className={`${baseClassName}__body__topic`}>
            <MeetingItemInput meetingItem={meetingItem} readonly />
          </div>
          {hasAttachments && (
            <div className={`${baseClassName}__body__attachments`}>
              <AttachmentsContainer
                attachments={attachments.toJS()}
                options={{
                  showPreview: showAttachmentsAsList ? false : true,
                  view: showAttachmentsAsList ? 'list' : 'grid',
                }}
              />
            </div>
          )}
        </div>
      </WpContainerBody>
      <WpContainerFooter justifyContent="spaceBetween">
        <WpButton
          variant="tertiary"
          onClick={handlePreviousItemClick}
          disabled={isPreviousDisabled || isNavigatingForward}
          loading={isNavigatingBack}
        >
          <WpIcon name="angle-left" />
          {isSmall ? 'Previous' : 'Previous Topic'}
        </WpButton>
        <div className={`${baseClassName}__cta-wrapper`}>
          <div className={`${baseClassName}__cta-wrapper__checkbox`}>
            <WpCheckbox
              id="complete_topic_checkbox"
              label="Complete topic"
              checked={isCompleteChecked}
              onChange={toggleIsCompleteChecked}
            />
          </div>
          <WpButton onClick={handleNextItemClick} loading={isNavigatingForward} disabled={isNavigatingBack}>
            {isLastItem ? 'Wrap up' : <> {isSmall ? 'Next' : 'Next Topic'}</>}
            <WpIcon name="angle-right" />
          </WpButton>
        </div>
      </WpContainerFooter>
    </WpContainer>
  );
};

export default TopicDetails;
